import React, { useState, useEffect } from 'react'
import {
    Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
    IconButton, Typography, FormControl, InputLabel, Select, MenuItem,
    FormControlLabel, Checkbox, CircularProgress, Snackbar,
} from '@material-ui/core'
import EditIcon from '@material-ui/icons/Edit'
import AddCircleIcon from '@material-ui/icons/AddCircle'
import AccountCircleIcon from '@material-ui/icons/AccountCircle'
import { getAllUsers, createAppUser, updateUserFirestoreDoc, changeUserPassword } from 'common/api/userApi'

const PANELS = [
    { key: 'dashboard', label: 'Inicio' },
    { key: 'notes', label: 'Notas' },
    { key: 'repairs', label: 'Reparaciones' },
    { key: 'taskSign', label: 'Firmar Tareas' },
    { key: 'stock', label: 'Stock' },
    { key: 'clients', label: 'Clientes' },
    { key: 'invoices', label: 'Facturas' },
    { key: 'proformas', label: 'Proformas' },
    { key: 'tickets', label: 'Tickets' },
    { key: 'controlTime', label: 'Control de Tiempo' },
    { key: 'reacondicionados', label: 'Reacondicionados' },
]

const DEFAULT_PERMISSIONS = PANELS.reduce((acc, p) => ({ ...acc, [p.key]: true }), {})

const emptyForm = () => ({
    displayName: '',
    email: '',
    password: '',
    newPassword: '',
    role: 'admin',
    permissions: { ...DEFAULT_PERMISSIONS },
})

const ROLE_LABELS = { super: 'Super', admin: 'Admin', 'control-time': 'Control tiempo', user: 'Usuario' }

export default function UsersManagementSection() {
    const [users, setUsers] = useState([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [editEmail, setEditEmail] = useState(null) // null = crear, string = editar
    const [form, setForm] = useState(emptyForm())
    const [snack, setSnack] = useState({ open: false, msg: '', error: false })

    const load = async () => {
        setLoading(true)
        try {
            const list = await getAllUsers()
            setUsers(list)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => { load() }, [])

    const openCreate = () => {
        setEditEmail(null)
        setForm(emptyForm())
        setDialogOpen(true)
    }

    const openEdit = (user) => {
        setEditEmail(user.id)
        setForm({
            displayName: user.displayName || '',
            email: user.id,
            password: '',
            newPassword: '',
            role: user.role || 'admin',
            permissions: user.permissions ? { ...DEFAULT_PERMISSIONS, ...user.permissions } : { ...DEFAULT_PERMISSIONS },
        })
        setDialogOpen(true)
    }

    const closeDialog = () => {
        setDialogOpen(false)
        setEditEmail(null)
    }

    const handleFormChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }))
    }

    const handlePermissionChange = (key, value) => {
        setForm(prev => ({ ...prev, permissions: { ...prev.permissions, [key]: value } }))
    }

    const handleSave = async () => {
        if (!form.email.trim()) return setSnack({ open: true, msg: 'El email es obligatorio', error: true })
        if (!editEmail && !form.password.trim()) return setSnack({ open: true, msg: 'La contraseña es obligatoria', error: true })

        setSaving(true)
        try {
            if (editEmail) {
                await updateUserFirestoreDoc(editEmail, {
                    role: form.role,
                    displayName: form.displayName || '',
                    permissions: form.permissions,
                })
                if (form.newPassword.trim()) {
                    await changeUserPassword(editEmail, form.newPassword.trim())
                }
                setSnack({ open: true, msg: 'Usuario actualizado', error: false })
            } else {
                await createAppUser(form.email.trim().toLowerCase(), form.password, {
                    role: form.role,
                    displayName: form.displayName || '',
                    permissions: form.role === 'admin' ? form.permissions : null,
                })
                setSnack({ open: true, msg: 'Usuario creado correctamente', error: false })
            }

            closeDialog()
            load()
        } catch (e) {
            console.error(e)
            const msg = e.details || (
                e.code === 'auth/email-already-in-use' ? 'Ya existe un usuario con ese email' :
                e.code === 'auth/weak-password' ? 'La contraseña debe tener al menos 6 caracteres' :
                'Error al guardar el usuario'
            )
            setSnack({ open: true, msg, error: true })
        } finally {
            setSaving(false)
        }
    }

    const showPermissions = form.role === 'admin' || !!editEmail

    return (
        <div className="section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <h3 style={{ margin: 0 }}>Usuarios</h3>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddCircleIcon />}
                    onClick={openCreate}
                >
                    Añadir usuario
                </Button>
            </div>

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
                    <CircularProgress />
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {users.map(user => (
                        <div key={user.id} style={{
                            display: 'flex', alignItems: 'center', gap: 12,
                            padding: '12px 16px', borderRadius: 8,
                            border: '1px solid #e0e0e0', background: '#fafafa',
                        }}>
                            <AccountCircleIcon style={{ fontSize: 36, color: '#9e9e9e' }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                                    {user.displayName || <span style={{ color: '#aaa', fontStyle: 'italic' }}>Sin nombre</span>}
                                </Typography>
                                <Typography variant="body2" style={{ color: '#666', fontSize: 13 }}>
                                    {user.id}
                                </Typography>
                            </div>
                            <span style={{
                                fontSize: 12, fontWeight: 600, padding: '2px 10px',
                                borderRadius: 12, background: '#e3f2fd', color: '#1565c0',
                            }}>
                                {ROLE_LABELS[user.role] || user.role}
                            </span>
                            <IconButton size="small" onClick={() => openEdit(user)}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </div>
                    ))}
                    {users.length === 0 && (
                        <p style={{ color: '#888', textAlign: 'center' }}>No hay usuarios registrados.</p>
                    )}
                </div>
            )}

            <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{editEmail ? 'Editar usuario' : 'Nuevo usuario'}</DialogTitle>
                <DialogContent>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
                        <TextField
                            label="Nombre de usuario"
                            value={form.displayName}
                            onChange={e => handleFormChange('displayName', e.target.value)}
                            variant="outlined"
                            size="small"
                            fullWidth
                        />
                        <TextField
                            label="Email"
                            value={form.email}
                            onChange={e => handleFormChange('email', e.target.value)}
                            variant="outlined"
                            size="small"
                            fullWidth
                            disabled={!!editEmail}
                            type="email"
                        />
                        {!editEmail && (
                            <TextField
                                label="Contraseña"
                                value={form.password}
                                onChange={e => handleFormChange('password', e.target.value)}
                                variant="outlined"
                                size="small"
                                fullWidth
                                type="password"
                            />
                        )}
                        <FormControl variant="outlined" size="small" fullWidth>
                            <InputLabel>Rol</InputLabel>
                            <Select
                                value={form.role}
                                onChange={e => handleFormChange('role', e.target.value)}
                                label="Rol"
                            >
                                <MenuItem value="admin">Admin</MenuItem>
                                <MenuItem value="control-time">Control tiempo</MenuItem>
                                <MenuItem value="user">Usuario</MenuItem>
                            </Select>
                        </FormControl>

                        {showPermissions && (
                            <div>
                                <Typography variant="subtitle2" style={{ marginBottom: 8, fontWeight: 600 }}>
                                    Paneles accesibles
                                </Typography>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
                                    {PANELS.map(panel => (
                                        <FormControlLabel
                                            key={panel.key}
                                            style={{ width: '50%', margin: 0 }}
                                            control={
                                                <Checkbox
                                                    checked={Boolean(form.permissions[panel.key])}
                                                    onChange={e => handlePermissionChange(panel.key, e.target.checked)}
                                                    color="primary"
                                                    size="small"
                                                />
                                            }
                                            label={<span style={{ fontSize: 14 }}>{panel.label}</span>}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        {editEmail && (
                            <div style={{ marginTop: 4 }}>
                                <TextField
                                    label="Nueva contraseña (opcional)"
                                    value={form.newPassword}
                                    onChange={e => handleFormChange('newPassword', e.target.value)}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    type="password"
                                    helperText="Déjalo vacío para no cambiar la contraseña"
                                />
                            </div>
                        )}
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog} disabled={saving}>Cancelar</Button>
                    <Button onClick={handleSave} color="primary" variant="contained" disabled={saving}>
                        {saving ? <CircularProgress size={20} /> : 'Guardar'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snack.open}
                autoHideDuration={4000}
                onClose={() => setSnack(s => ({ ...s, open: false }))}
                message={snack.msg}
                ContentProps={{ style: { background: snack.error ? '#d32f2f' : '#388e3c' } }}
            />
        </div>
    )
}
